/**
 * Corpus Health data hooks (frontend.md §5 step 10).
 *
 * The Health surface polls (frontend.md §1.5 — "Corpus Health and Source Management poll
 * ... with a capability-tiered interval, paused when the tab/route is not visible"). Every
 * query here takes `refetchInterval` from the capability tier's `pollIntervalMs`; the
 * surface passes `enabled` = route-visible so polling pauses when the surface is hidden.
 *
 * The mutation hooks (`useRetryExtractionMutation`, `useRestoreNoteMutation`,
 * `useResolveConflictMutation`) invalidate the matching health queries on success so the
 * panels reflect the action immediately (frontend.md §2.2 — "mutations ... with optimistic
 * updates + invalidation").
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  emptyTrash,
  fetchConflictedNotes,
  fetchCountReport,
  fetchExtractionQueue,
  fetchFailedExtractions,
  fetchInferenceMetrics,
  fetchSoftDeleted,
  fetchSoftDeletedNotebooks,
  hardDeleteNote,
  resolveConflict,
  restoreNote,
  restoreNotebook,
  retryExtraction,
} from '@/api';
import type {
  ConflictedNoteList,
  CountReport,
  CountReportName,
  ExtractionQueueStatus,
  FailedExtractionList,
  InferenceMetrics,
  NoteId,
  NotebookId,
  ResolveConflictRequest,
  RetryExtractionRequest,
  SoftDeletedNoteList,
  SoftDeletedNotebookList,
  SourceActionResult,
} from '@/types';
import { queryKeys } from '@/providers';
import { RENDER_DEFAULTS } from '@/config/renderDefaults';
import { useErrorHandler } from './useErrorHandler';

/** Common options every Health query takes — polling is paused when the route is hidden. */
interface HealthQueryOptions {
  /** True when the Health surface is the active route — polling pauses otherwise. */
  enabled: boolean;
}

/**
 * The live ingest-metrics poll behind ``<IngestMetricsPanel>``. Polls at a fixed 1-second
 * cadence (not the capability-tier interval) — the panel is a live monitor with a
 * time-axis chart; per-second sampling is required for the scrolling line to feel
 * continuous. ``staleTime: 0`` so the deltas the panel computes are real timestamps not
 * cached re-reads. ``refetchIntervalInBackground: false`` so a hidden tab doesn't keep
 * polling and burning the queue table.
 */
export function useInferenceMetricsQuery(
  opts: HealthQueryOptions,
): UseQueryResult<InferenceMetrics, Error> {
  const { handleError } = useErrorHandler();
  return useQuery({
    queryKey: queryKeys.inferenceMetrics(),
    enabled: opts.enabled,
    refetchInterval: opts.enabled ? 1000 : false,
    refetchIntervalInBackground: false,
    staleTime: 0,
    queryFn: async () => {
      try {
        return await fetchInferenceMetrics();
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
  });
}

export function useExtractionQueueQuery(
  opts: HealthQueryOptions,
): UseQueryResult<ExtractionQueueStatus, Error> {
  const { pollIntervalMs } = RENDER_DEFAULTS;
  const { handleError } = useErrorHandler();
  return useQuery({
    queryKey: queryKeys.extractionQueue(),
    enabled: opts.enabled,
    refetchInterval: opts.enabled ? pollIntervalMs : false,
    queryFn: async () => {
      try {
        return await fetchExtractionQueue();
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
  });
}

export function useFailedExtractionsQuery(
  page: number,
  opts: HealthQueryOptions,
): UseQueryResult<FailedExtractionList, Error> {
  const { pollIntervalMs, defaultPageSize } = RENDER_DEFAULTS;
  const { handleError } = useErrorHandler();
  return useQuery({
    queryKey: queryKeys.failedExtractions(page),
    enabled: opts.enabled,
    refetchInterval: opts.enabled ? pollIntervalMs : false,
    queryFn: async () => {
      try {
        return await fetchFailedExtractions(defaultPageSize, page * defaultPageSize);
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
  });
}

export function useSoftDeletedQuery(
  page: number,
  opts: HealthQueryOptions,
): UseQueryResult<SoftDeletedNoteList, Error> {
  const { defaultPageSize } = RENDER_DEFAULTS;
  const { handleError } = useErrorHandler();
  return useQuery({
    queryKey: queryKeys.softDeleted(page),
    enabled: opts.enabled,
    queryFn: async () => {
      try {
        return await fetchSoftDeleted(defaultPageSize, page * defaultPageSize);
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
  });
}

export function useConflictedNotesQuery(
  page: number,
  opts: HealthQueryOptions,
): UseQueryResult<ConflictedNoteList, Error> {
  const { defaultPageSize } = RENDER_DEFAULTS;
  const { handleError } = useErrorHandler();
  return useQuery({
    queryKey: queryKeys.conflictedNotes(page),
    enabled: opts.enabled,
    queryFn: async () => {
      try {
        return await fetchConflictedNotes(defaultPageSize, page * defaultPageSize);
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
  });
}

export function useCountReportQuery(
  report: CountReportName,
  opts: HealthQueryOptions,
): UseQueryResult<CountReport, Error> {
  const { handleError } = useErrorHandler();
  return useQuery({
    queryKey: queryKeys.countReport(report),
    enabled: opts.enabled,
    queryFn: async () => {
      try {
        return await fetchCountReport(report);
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
  });
}

// --- mutations --------------------------------------------------------------

export function useRetryExtractionMutation(): UseMutationResult<
  SourceActionResult,
  Error,
  RetryExtractionRequest
> {
  const qc = useQueryClient();
  const { handleError } = useErrorHandler();
  return useMutation({
    mutationFn: (req: RetryExtractionRequest) => retryExtraction(req),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['health'] });
    },
    onError: (err) => handleError(err),
  });
}

export function useRestoreNoteMutation(): UseMutationResult<SourceActionResult, Error, NoteId> {
  const qc = useQueryClient();
  const { handleError } = useErrorHandler();
  return useMutation({
    mutationFn: (noteId: NoteId) => restoreNote(noteId),
    onSuccess: () => {
      // Both the Trash list AND the notebooks tree need to refresh — restore brings the
      // note back into its original notebook (notebooks tree picks it up via the
      // useNotebookNotesQuery that targets its notebook_id).
      void qc.invalidateQueries({ queryKey: ['health', 'soft-deleted'] });
      void qc.invalidateQueries({ queryKey: ['notebook'] });
      void qc.invalidateQueries({ queryKey: ['note-titles'] });
    },
    onError: (err) => handleError(err),
  });
}

/**
 * `<TrashSection>`'s per-note hard-delete (friction-log fix #90, 2026-05-29). On success
 * the Trash list refreshes (the row disappears). No other queries depend on the deleted
 * note's id — the FK cascades on the backend removed every downstream row, and any
 * frontend cache that held the note's data was already showing a soft-deleted state.
 */
export function useHardDeleteNoteMutation(): UseMutationResult<SourceActionResult, Error, NoteId> {
  const qc = useQueryClient();
  const { handleError } = useErrorHandler();
  return useMutation({
    mutationFn: (noteId: NoteId) => hardDeleteNote(noteId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['health', 'soft-deleted'] });
    },
    onError: (err) => handleError(err),
  });
}

/**
 * `<TrashSection>`'s "Empty Trash" right-click action (friction-log fix #90, 2026-05-29).
 * Hard-removes every soft-deleted note in one statement; backend returns the count in
 * `detail.removed_count`. The UI confirms before calling — no server-side confirmation.
 */
export function useEmptyTrashMutation(): UseMutationResult<SourceActionResult, Error, void> {
  const qc = useQueryClient();
  const { handleError } = useErrorHandler();
  return useMutation({
    mutationFn: () => emptyTrash(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['health', 'soft-deleted'] });
    },
    onError: (err) => handleError(err),
  });
}

/**
 * `<TrashSection>` extension — soft-deleted notebooks for the recovery view (#95,
 * 2026-05-29). Notebooks are small in count so we don't paginate. Polling stays paused
 * unless the Trash folder is expanded (the caller passes `enabled`).
 */
export function useSoftDeletedNotebooksQuery(
  opts: HealthQueryOptions,
): UseQueryResult<SoftDeletedNotebookList, Error> {
  const { handleError } = useErrorHandler();
  return useQuery({
    queryKey: ['health', 'soft-deleted-notebooks'],
    enabled: opts.enabled,
    queryFn: async () => {
      try {
        return await fetchSoftDeletedNotebooks();
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
  });
}

/**
 * Restore a soft-deleted notebook (#95, 2026-05-29). The notes inside come back
 * automatically — soft-delete preserved their `notebook_id`. Invalidates the notebook
 * tree + the soft-deleted notebook list so the UI catches up immediately.
 */
export function useRestoreNotebookMutation(): UseMutationResult<
  SourceActionResult,
  Error,
  NotebookId
> {
  const qc = useQueryClient();
  const { handleError } = useErrorHandler();
  return useMutation({
    mutationFn: (notebookId: NotebookId) => restoreNotebook(notebookId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['health', 'soft-deleted-notebooks'] });
      void qc.invalidateQueries({ queryKey: ['notebooks'] });
      void qc.invalidateQueries({ queryKey: ['list_notebooks'] });
    },
    onError: (err) => handleError(err),
  });
}

export function useResolveConflictMutation(): UseMutationResult<
  SourceActionResult,
  Error,
  ResolveConflictRequest
> {
  const qc = useQueryClient();
  const { handleError } = useErrorHandler();
  return useMutation({
    mutationFn: (req: ResolveConflictRequest) => resolveConflict(req),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['health', 'conflicted-notes'] });
    },
    onError: (err) => handleError(err),
  });
}
